import { FC, Fragment } from 'react';
import Icon from 'components/icons';
import Notification from 'components/notification';
import { SSO_DOCUMENTATION_URL } from 'constants/settings';
import {
  CopyTagButton,
  CopyTagText,
  RoundedTag,
  SsoInformationContainer,
} from '../AccountSsoCard/styles';
import { ENTRA_CLAIM_URIS, isEntraProvider } from './helper';

const ACS_URL = 'https://auth.ine.com/saml2/idpresponse';
const ENTITY_ID = 'urn:amazon:cognito:sp:us-east-1_gxIiEWftA';

const copyToClipboard = async (value: string) => {
  try {
    await navigator.clipboard.writeText(value);
    Notification({ type: 'success', text: 'Copied to clipboard' });
  } catch {
    Notification({ type: 'error', text: 'Couldn´t copy. Select the text and copy it manually.' });
  }
};

// Lets long values such as claim URIs wrap after a "/" instead of mid-word.
const withSlashBreaks = (text: string) =>
  text.split('/').map((part, index, parts) => (
    <Fragment key={index}>
      {part}
      {index < parts.length - 1 && (
        <>
          /<wbr />
        </>
      )}
    </Fragment>
  ));

interface ICopyTag {
  value: string;
  label?: string;
  className?: string;
}

// Copies only the value, not the visible label.
const CopyTag: FC<ICopyTag> = ({ value, label, className }) => (
  <CopyTagButton
    type="button"
    className={className}
    aria-label={`Copy ${label || value}`}
    onClick={() => copyToClipboard(value)}
  >
    <RoundedTag className="sso-tag" color="#1A2535" style={{ whiteSpace: 'normal' }}>
      <Icon size="small" icon="Copy" />
      <CopyTagText>
        {label && `${label}: `}
        {withSlashBreaks(value)}
      </CopyTagText>
    </RoundedTag>
  </CopyTagButton>
);

interface ISsoProviderInformation {
  providerName?: string;
}

const SsoProviderInformation: FC<ISsoProviderInformation> = ({ providerName }) => {
  return (
    <SsoInformationContainer>
      <span className="w-500">
        Create a Service Provider APP for INE in your Identity Provider (Idp) using these values:
      </span>
      <CopyTag className="mt-5" label="ACS URL (Reply URL)" value={ACS_URL} />
      <CopyTag className="mt-2" label="Entity ID" value={ENTITY_ID} />
      <div className="attributes-section">
        <div className="attributes-list">
          <span>
            IMPORTANT: Your identity provider must send these three claims. Enter each claim&apos;s
            name in the form exactly as it is sent:
          </span>
          <ul>
            <li>First name</li>
            <li>Last name</li>
            <li>Email address</li>
          </ul>
          {isEntraProvider(providerName) && (
            <>
              <span className="d-block mt-2">Microsoft Entra ID sends them as:</span>
              {Object.values(ENTRA_CLAIM_URIS).map((uri) => (
                <CopyTag key={uri} className="mt-2" value={uri} />
              ))}
            </>
          )}
        </div>
      </div>
      <div className="documentation-section">
        For more information refer to{' '}
        <a href={SSO_DOCUMENTATION_URL} target="_blank" rel="noreferrer">
          the documentation
        </a>
      </div>
    </SsoInformationContainer>
  );
};

export default SsoProviderInformation;
